import {
    AutoTocEntry,
    CodeArticle,
    PublicationSheet,
    PublicationSheetWithId,
    Tree,
    TreeData,
} from '@livv/models';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import { createContext, FC, useContext, useEffect, useMemo } from 'react';
import { BookPageProps } from '@screens/Books/Book';
import { DecisionsCitationsInPublicationSheet } from '@utils/serverSide/book/getDecisionsCitationsInPublicationSheet';
import { PreviewsContent } from '@utils/serverSide/book/getPreviewsContent';
import { convertTimestamps } from '@utils/serverSide/utils';
import { pruneTreeEager } from '@utils/trees';

interface BookContextProps {
    autoToc?: AutoTocEntry[];
    bookData: TreeData;
    bookId: string;
    bookMeta: Omit<Tree, 'content'>;
    decisionsCitationsInPublicationSheet?: DecisionsCitationsInPublicationSheet[];
    isLegitext: boolean;
    previewsContent?: PreviewsContent;
    sheetData?: CodeArticle | PublicationSheet;
    sheets?: PublicationSheetWithId[];
}

const BookContext = createContext<BookContextProps | null>(null);

export const useBookPageContext = (): BookContextProps => {
    const value = useContext(BookContext);
    if (!value) throw new Error('Book context cannot be empty');

    return value;
};

const BookContextProvider: FC<BookPageProps> = ({
    autoToc,
    book,
    bookId,
    children,
    decisionsCitationsInPublicationSheet,
    previewsContent,
    sheet,
    sheets,
}) => {
    const router = useRouter();

    // To be able to call methods on Timestamp after been serialized by `JSON.parse(JSON.stringify())`
    // in getBookServerSideProps, need to re-initialize the Timestamp class object
    const bookWithCorrectDateFormat = convertTimestamps(book);
    const sheetWithCorrectDateFormat = convertTimestamps(sheet);

    const treeData = new TreeData(book?.content ?? [], book?.title ?? '');
    if (book?.isKeyword) {
        treeData.content = pruneTreeEager(treeData.content);
    }

    const bookMeta = omit(bookWithCorrectDateFormat, 'content');

    const isLegitext = bookId?.includes('LEGITEXT');

    const contextValue = useMemo<BookContextProps>(
        () => ({
            autoToc,
            bookData: treeData,
            bookId,
            bookMeta,
            decisionsCitationsInPublicationSheet,
            isLegitext,
            previewsContent,
            sheetData: sheetWithCorrectDateFormat,
            sheets,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            autoToc,
            bookId,
            bookMeta,
            decisionsCitationsInPublicationSheet,
            isLegitext,
            sheet,
            sheets,
        ],
    );

    useEffect(() => {
        if (!book || treeData.content.length === 0) {
            router.push('/404');
        }
    }, [book, treeData.content.length, router]);

    return <BookContext.Provider value={contextValue}>{children}</BookContext.Provider>;
};

export default BookContextProvider;
